'use client';

import { useEffect } from 'react';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useExposureEvent from '@alltrails/experiments/hooks/useExposureEvent';
import InstallBanner from '@alltrails/install-prompt/components/InstallBanner';
import useInstallPrompt from '@alltrails/install-prompt/hooks/useInstallPrompt';
import type { TrailId } from '@alltrails/types/TrailId';
import useInstallDrawerV2PrimaryCta from '@/hooks/useInstallDrawerV2PrimaryCta';
import useScrollAuthAltTreatment from '@/hooks/useScrollAuthAltTreatment';

type TrailInstallPromptProps = {
  isBot: boolean;
  trailId: TrailId;
};

const TrailInstallPrompt = ({ isBot, trailId }: TrailInstallPromptProps) => {
  const { isEligible, isMiniInterstitial, isDismissibleWall } = useScrollAuthAltTreatment(isBot);
  const [isBannerOpen, setIsBannerOpen] = useInstallPrompt('banner', !isBot);
  const isMobile = useIsMobileSizedScreen();
  const sendExposureEvent = useExposureEvent('web-growth-rm-mweb-install-drawer-v2');
  const showUnauthPrimaryCta = useInstallDrawerV2PrimaryCta();

  // For scroll-auth-v2 Treatments 2 and 3, the install drawer is fully suppressed.
  const isSuppressedByScrollAuthAlt = isEligible && (isMiniInterstitial || isDismissibleWall);

  useEffect(() => {
    if (isMobile) {
      sendExposureEvent();
    }
  }, [isMobile, sendExposureEvent]);

  // Unauth primary_cta uses a single MobileFooter "Open in app" CTA instead of the drawer.
  if (isBot || isSuppressedByScrollAuthAlt || showUnauthPrimaryCta) {
    return null;
  }

  return (
    <InstallBanner
      deeplinkData={{ pageId: trailId, pageType: 'trail' }}
      isOpen={isBannerOpen}
      onRequestClose={() => setIsBannerOpen(false)}
      promptLocation="trail page"
    />
  );
};

export default TrailInstallPrompt;
